import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetTenantSettingsQuery,
  useUpdateTenantMutation,
} from '@/redux/internal-services/onboardingApi';
import { StepShell } from './StepShell';
import type { StepHandle, SubmitResult } from './stepHandle';

interface Props {
  onBack?: () => void;
  onNext: () => void;
  onSkip?: () => void;
  disabled?: boolean;
}

/**
 * Guided-setup "Service Territory" step — the geographic areas the business serves. The user
 * types free-form territory names (comma-separated) and adds them to the list. Persists to
 * tenant.service_territories as a plain string list. Exposes a {@link StepHandle} so the voice
 * agent can add/remove territories by name and submit.
 */
const ServiceTerritoryStep = forwardRef<StepHandle, Props>(({ onBack, onNext, onSkip, disabled = false }, ref) => {
  const { t } = useTranslation();
  const { data: tenantSettings } = useGetTenantSettingsQuery();
  const [updateTenant, { isLoading: saving }] = useUpdateTenantMutation();

  const [territories, setTerritories] = useState<string[]>([]);
  const [input, setInput] = useState('');
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Synchronous mirror so submit/getState see the freshest list after rapid voice actions.
  const territoriesRef = useRef<string[]>(territories);
  useEffect(() => { territoriesRef.current = territories; }, [territories]);

  useEffect(() => {
    const d = tenantSettings?.data as Record<string, unknown> | undefined;
    if (Array.isArray(d?.service_territories)) {
      // Tolerate legacy {state_name, country_name, …} objects from before this step
      // stored plain strings.
      setTerritories(
        (d!.service_territories as unknown[])
          .map((t) =>
            typeof t === 'string'
              ? t
              : [(t as Record<string, string>)?.state_name, (t as Record<string, string>)?.country_name]
                  .filter(Boolean)
                  .join(', ')
          )
          .filter(Boolean)
      );
    }
  }, [tenantSettings]);

  // Voice actuators — add/remove a territory by name (mirror kept in sync synchronously).
  const addTerritoryByName = (name: string) => {
    const v = name.trim();
    if (!v) return;
    if (territoriesRef.current.some((t) => t.toLowerCase() === v.toLowerCase())) return;
    const next = [...territoriesRef.current, v];
    territoriesRef.current = next;
    setTerritories(next);
  };

  const removeTerritoryByName = (name: string) => {
    const next = territoriesRef.current.filter((t) => t.toLowerCase() !== name.trim().toLowerCase());
    territoriesRef.current = next;
    setTerritories(next);
  };

  const addFromInput = () => {
    const parts = input
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    setTerritories((prev) => {
      const next = [...prev];
      for (const p of parts) {
        if (!next.some((t) => t.toLowerCase() === p.toLowerCase())) next.push(p);
      }
      return next;
    });
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addFromInput();
    } else if (e.key === 'Backspace' && !input && territories.length > 0) {
      // Empty box → backspace removes the last chip, token-input style.
      e.preventDefault();
      setTerritories((prev) => prev.slice(0, -1));
    }
  };

  const removeTerritory = (idx: number) =>
    setTerritories((prev) => prev.filter((_, i) => i !== idx));

  const startEdit = (idx: number) => {
    setEditingIdx(idx);
    setEditValue(territories[idx]);
  };

  const commitEdit = () => {
    if (editingIdx === null) return;
    const v = editValue.trim();
    setTerritories((prev) => {
      if (!v) return prev.filter((_, i) => i !== editingIdx);
      // Drop if the edited value now duplicates another entry.
      if (prev.some((t, i) => i !== editingIdx && t.toLowerCase() === v.toLowerCase())) {
        return prev.filter((_, i) => i !== editingIdx);
      }
      return prev.map((t, i) => (i === editingIdx ? v : t));
    });
    setEditingIdx(null);
    setEditValue('');
  };

  const persist = async (): Promise<SubmitResult> => {
    setError('');
    try {
      // Fold any text still in the box into the list before saving.
      const pending = input
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean);
      const merged = [...territoriesRef.current];
      for (const p of pending) {
        if (!merged.some((t) => t.toLowerCase() === p.toLowerCase())) merged.push(p);
      }
      await updateTenant({ service_territories: merged }).unwrap();
      return { ok: true };
    } catch {
      setError(t('common.failedToSave'));
      return { ok: false, reason: 'transient', message: 'Failed to save service territory.' };
    }
  };

  const handleNext = () => { void persist().then((r) => { if (r.ok) onNext(); }); };

  useImperativeHandle(ref, (): StepHandle => ({
    getState: () => ({ territories: territoriesRef.current }),
    submit: persist,
    addTerritory: addTerritoryByName,
    removeTerritory: removeTerritoryByName,
  }));

  return (
    <StepShell
      title="Service Territory"
      subtitle="Tell us about your service territory"
      onBack={onBack}
      onNext={handleNext}
      onSkip={onSkip}
      disabled={disabled}
      saving={saving}
      error={error}
    >
      <label className="block text-sm font-medium text-ui-text mb-1.5">
        Add your Service Territories
      </label>
      <div
        onClick={() => inputRef.current?.focus()}
        className="flex flex-wrap items-center gap-2 w-full min-h-[44px] border border-[#EDEDED] dark:border-white/10 rounded-lg px-2.5 py-2 text-sm bg-ui-input cursor-text focus-within:ring-2 focus-within:ring-brand-dark/40 focus-within:border-brand-dark"
      >
        {territories.map((t, idx) =>
          editingIdx === idx ? (
            <input
              key={`edit-${idx}`}
              type="text"
              autoFocus
              value={editValue}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={commitEdit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitEdit();
                } else if (e.key === 'Escape') {
                  setEditingIdx(null);
                  setEditValue('');
                }
              }}
              className="px-2.5 py-1 text-sm rounded-full border border-brand-dark focus:outline-none focus:ring-2 focus:ring-brand-dark/40"
            />
          ) : (
            <span
              key={`${t}-${idx}`}
              onDoubleClick={() => startEdit(idx)}
              title="Double-click to edit"
              className="inline-flex items-center gap-1 px-3 py-1 bg-brand-light text-brand-dark text-sm font-medium rounded-full cursor-pointer select-none"
            >
              {t}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeTerritory(idx); }}
                className="ml-0.5 hover:opacity-70"
              >
                <svg viewBox="0 0 12 12" fill="currentColor" className="w-2.5 h-2.5">
                  <path d="M8.485 3.515a.75.75 0 00-1.06 0L6 4.94 4.575 3.515a.75.75 0 10-1.06 1.06L4.94 6 3.515 7.425a.75.75 0 101.06 1.06L6 7.06l1.425 1.425a.75.75 0 101.06-1.06L7.06 6l1.425-1.425a.75.75 0 000-1.06z" />
                </svg>
              </button>
            </span>
          )
        )}
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={territories.length === 0 ? 'e.g. Dallas, Fort Worth, Plano' : ''}
          className="flex-1 min-w-[120px] border-0 p-0 text-sm text-ui-text placeholder-ui-text3 focus:outline-none focus:ring-0 bg-transparent"
        />
      </div>
      <p className="text-xs text-ui-text3 mt-1.5">
        Separate multiple territories with a comma, or press Enter to add. Double-click a
        territory to edit it. Backspace on an empty box removes the last one.
      </p>
    </StepShell>
  );
});

ServiceTerritoryStep.displayName = 'ServiceTerritoryStep';

export default ServiceTerritoryStep;
